import { Component, Inject, Input, OnChanges, OnInit } from '@angular/core';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import { DOCUMENT } from '@angular/common';
import { HighchartsThemeService } from '@frk/eds-components';
import { fromEvent, Subject } from 'rxjs';
import { WINDOW } from '@ng-web-apis/common';
import textZoomEvent from 'text-zoom-event/dist/textZoomEvent.module.js';
import { takeUntil } from 'rxjs/operators';

const logger = Logger.getLogger('ColumnChartComponent');

@Component({
  selector: 'ft-fund-performance-column-chart',
  templateUrl: './fund-performance-column-chart.component.html',
  styleUrls: ['./fund-performance-column-chart.component.scss'],
})
export class FundPerformanceColumnChartComponent implements OnInit, OnChanges {
  highcharts;

  @Input() seriesData: Highcharts.SeriesOptionsType[];

  @Input() xAxisCategories: string[];

  private unsubscribe$: Subject<void> = new Subject<void>();

  chartOptions: Highcharts.Options;
  constructor(
    private highchartsTheme: HighchartsThemeService,
    @Inject(WINDOW) readonly windowRef: Window,
    @Inject(DOCUMENT) private documentRef: Document
  ) {}

  ngOnInit(): void {
    import(/* webpackChunkName: "highcharts-async" */ 'highcharts').then(
      (highchartsModule) => {
        highchartsModule.setOptions(this.highchartsTheme.themeOptions);
        require('highcharts/modules/accessibility')(highchartsModule);
        this.highcharts = highchartsModule;

        this.createChartOptions(highchartsModule);
        this.updateChartOptions();
      }
    );
    // initializing the textZoomEvent
    textZoomEvent.init();
    // will check if text has been zoomed in the browser
    fromEvent(this.documentRef, 'textzoom')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        // this is to force a Highcharts reflow event
        this.windowRef.dispatchEvent(new Event('resize'));
      });
  }

  ngOnChanges(): void {
    if (this.chartOptions) {
      this.updateChartOptions();
    }
  }

  private updateChartOptions() {
    this.chartOptions.series = this.seriesData;
    if ('categories' in this.chartOptions.xAxis) {
      this.chartOptions.xAxis.categories = this.xAxisCategories;
      // cloned to trigger onChange
      this.chartOptions = cloneDeep(this.chartOptions);
    }
  }

  private createChartOptions(highchartsModule) {
    let currentSeperator = '.';
    const seperatorStr = 'seperator';
    const dataStr = 'data';
    if (this.seriesData?.length > 0) {
      const seperator = this.seriesData[0][dataStr]?.filter((dp) => dp.y)[0][
        seperatorStr
      ];
      currentSeperator = seperator ? seperator : currentSeperator;
    }
    this.chartOptions = {
      title: null,
      series: [],
      chart: {
        events: {
          render(this) {
            const that = this;
            setTimeout(() => {
              that.reflow();
            }, 0);
          },
        },
        reflow: true,
      },
      tooltip: {
        formatter() {
          const decimalKey = 'decimals';
          const seperator = 'seperator';
          return `${this.point.name}<br />${highchartsModule.numberFormat(
            this.point.y,
            this.point[decimalKey],
            this.point[seperator]
          )}`;
        },
      },
      xAxis: {
        categories: [],
        labels: {
          useHTML: true,
          style: {
            color: this.highchartsTheme.themeFonts.colors.grey,
            fontSize: this.highchartsTheme.themeFonts.size.p,
            fontFamily: this.highchartsTheme.themeFonts.fontFamily,
          },
        },
      },
      yAxis: {
        gridLineDashStyle: 'LongDash',
        minTickInterval: 0.1,
        title: {
          text: null,
        },
        labels: {
          useHTML: true,
          style: {
            color: this.highchartsTheme.themeFonts.colors.grey,
            fontSize: this.highchartsTheme.themeFonts.size.p,
            fontFamily: this.highchartsTheme.themeFonts.fontFamily,
          },
          formatter() {
            return `${highchartsModule.numberFormat(
              this.value,
              1,
              currentSeperator
            )}`;
          },
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          maxPointWidth: 24,
        },
        series: {
          events: {
            legendItemClick() {
              return false;
            },
          },
          animation: {
            duration: 2000,
            easing: 'easeIn',
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
    };
  }
}
